import { useState } from "react";
import axios from "axios";
import { Mail } from "lucide-react";
import { useStore } from "../context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const { t, lang } = useStore();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim().length >= 2 && EMAIL_RE.test(form.email) && form.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setStatus("error"); setMsg(t.contact.validation); return; }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus("done");
      setMsg(t.contact.thanks);
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.status === 429 ? t.contact.tooMany : t.contact.error);
    }
  };

  const inputCls = "field w-full px-3 py-2.5 text-sm";
  const labelCls = "mb-1 block text-xs font-semibold text-[var(--muted)]";

  return (
    <section id="contact" className="scroll-mt-28 border-t border-[var(--line)] bg-[var(--paper)]" data-testid="contact-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:py-24">
        <div>
          <h2 className="font-display text-4xl sm:text-5xl" data-testid="contact-title">{t.contact.title}</h2>
          <p className="mt-2 max-w-md text-base text-[var(--muted)]">{t.contact.sub}</p>
          <p className="mt-4 flex items-center gap-2 text-sm text-[var(--muted)]">
            <Mail className="accent-text h-4 w-4" />
            {t.contact.or} <a href={`mailto:${t.footer.email}`} className="font-semibold text-[var(--ink)] underline-offset-4 hover:underline" data-testid="contact-email-link">{t.footer.email}</a>
          </p>
        </div>

        <form onSubmit={submit} className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:p-6" data-testid="contact-form" noValidate>
          <div>
            <label className={labelCls} htmlFor="ct-name">{t.contact.name}</label>
            <input id="ct-name" className={inputCls} value={form.name} onChange={set("name")} maxLength={80} autoComplete="name" data-testid="contact-name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="ct-email">{t.contact.email}</label>
            <input id="ct-email" type="email" className={inputCls} value={form.email} onChange={set("email")} maxLength={120} autoComplete="email" data-testid="contact-email" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="ct-message">{t.contact.message}</label>
            <textarea id="ct-message" className={`${inputCls} min-h-[140px] resize-none`} value={form.message} onChange={set("message")} maxLength={2000} placeholder={t.contact.messagePlaceholder} data-testid="contact-message" />
          </div>
          <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
            <span className="text-[11px] text-[var(--muted)]">{form.message.length}/2000</span>
            <button type="submit" disabled={status === "sending"} className="btn-primary px-6 py-3 text-sm" data-testid="contact-submit">
              {status === "sending" ? t.contact.sending : t.contact.send}
            </button>
          </div>
          {msg && (
            <p className={`text-sm sm:col-span-2 ${status === "error" ? "text-red-600" : "text-green-700"}`} data-testid="contact-message-status" role="status">{msg}</p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
